// xsiteio onboarding wizard. Shows Mary every value she needs to type
// into xsiteio's back-office UI, pre-formatted with copy-to-clipboard
// buttons, plus a checklist so she can pick up where she left off.
//
// This is the SIMPLE tier of automation while we wait to hear back
// from xsiteio about API access (Dave emailed Jose 2026-06-05). If
// xsiteio opens up a write API later, this dialog becomes a one-click
// "Onboard" button instead of a checklist.
//
// Flow Mary follows (matches the two onboarding videos Dave recorded):
//   1. xsiteio Clients page → Add Client (paste the External ID we generate)
//   2. xsiteio assigns a Numeric ID — Mary types it back into this dialog
//      so we can stamp it onto sites.xsiteio_site_id (future syncs use
//      it directly, no name matching needed)
//   3. Add Site form — copy each pre-filled field across
//   4. Billing & Subscription dialog — toggle the modules we already
//      track in XSite Command, copy the annual price + next charge date
//   5. (Later, in the second video) Log in as the new client user and
//      configure General/Fuel/Contacts/Sync/UPCs/Shelftag tabs. The
//      wizard surfaces the Fuel Tax Rate + a few defaults for that step
//
// State persistence: we store local-only checklist state in localStorage
// keyed by site id, so Mary closing the dialog mid-flow doesn't lose
// her place. The captured xsiteio_site_id (the only durable value) is
// persisted to the database via PUT /api/sites/:id.

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';

// Same algorithm as server/controllers/sitesController.js
// defaultXsiteIoCredential -- kept duplicated so the dialog can show
// the value before any PUT round-trip. Pattern (Dave 2026-06-04):
//   prov.lower + '-' + first-word-of-name.lower
//                    + digits(site_number, leading zeros stripped)
//                    + rest-of-name.lower
//                    + '@xsite.com'
// e.g. site_name='Centex Outlook' + site_number='cen00123' + prov='SK'
//      -> 'sk-centex123outlook@xsite.com'
function generateExternalId(site) {
  const prov    = String(site.province_state || '').toLowerCase().trim();
  const nameRaw = String(site.site_name || '').toLowerCase().trim();
  if (!prov || !nameRaw) return '';
  const words     = nameRaw.split(/\s+/).filter(Boolean);
  const firstWord = words[0] || '';
  const restName  = words.slice(1).join('');
  const digitsRaw = (String(site.site_number || '').match(/\d+/g) || []).join('');
  const numPart   = digitsRaw ? String(parseInt(digitsRaw, 10)) : '';
  if (!firstWord) return '';
  return `${prov}-${firstWord}${numPart}${restName}@xsite.com`;
}

// Default POS string xsiteio expects in their dropdown. Maps our
// pos_type column to xsiteio's enum.
function posForXsiteio(posType) {
  if (!posType) return '';
  const t = String(posType).toLowerCase();
  if (t.includes('esso'))   return 'ESSO';
  if (t.includes('petro'))  return 'PETROCANADA';
  if (t.includes('bulloch'))return 'BULLOCH';
  if (t.includes('passport'))return 'PASSPORT';
  return posType;
}

// One field row: label on the left, mono value, copy button.
function CopyRow({ label, value, hint }) {
  const [copied, setCopied] = useState(false);
  const empty = value == null || value === '';
  function doCopy() {
    if (empty) return;
    try {
      navigator.clipboard.writeText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* fall through silently */ }
  }
  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '180px 1fr auto',
      gap: 'var(--space-3)',
      alignItems: 'center',
      padding: '6px 0',
      borderBottom: '1px solid var(--color-border)'
    }}>
      <div style={{ color: 'var(--color-text-dim)', fontSize: 'var(--text-small)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </div>
      <div className="cell-mono" style={{
        color: empty ? 'var(--color-text-faint)' : 'var(--color-text)',
        fontSize: 'var(--text-body)',
        wordBreak: 'break-all'
      }}>
        {empty ? <em>— blank —</em> : String(value)}
        {hint && <span style={{ marginLeft: 8, color: 'var(--color-text-faint)', fontSize: 11 }}>({hint})</span>}
      </div>
      <button
        type="button"
        className="btn-secondary btn-sm"
        onClick={doCopy}
        disabled={empty}
        style={{ minWidth: 70 }}
      >
        {copied ? '✓ Copied' : 'Copy'}
      </button>
    </div>
  );
}

// "▶ Execute" action row -- one per automated step. Shows the button,
// running spinner text, success/failure summary, and any captured xsiteio
// IDs. Idempotent: clicking again is safe (backend short-circuits on
// already-done steps).
function ExecuteRow({ label, onClick, state, successText, errorPrefix = 'Failed', site, idDisplay }) {
  const running = !!(state && state.running);
  const ok      = state && state.ok === true;
  const failed  = state && state.ok === false;
  // Pre-onboard state: show the API field idDisplay (e.g. site.xsiteio_client_id)
  // so the row already reads "✓ done" when the site was onboarded via the CLI.
  const preExisting = !ok && !failed && idDisplay != null && idDisplay !== '';
  return (
    <div style={{
      background: 'var(--color-surface)',
      border: `1px solid ${failed ? 'var(--color-bad)' : (ok || preExisting) ? 'var(--color-good)' : 'var(--color-border)'}`,
      borderRadius: 'var(--radius-md)',
      padding: 'var(--space-3)',
      marginTop: 'var(--space-3)'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <button
          type="button"
          className="btn-primary"
          onClick={onClick}
          disabled={running}
        >
          {running ? 'Running…' : (ok || preExisting) ? '↻ Re-run' : '▶ Execute'}
        </button>
        <div style={{ fontWeight: 'var(--font-semibold)' }}>{label}</div>
      </div>
      {(ok || preExisting) && (
        <div style={{
          marginTop: 8,
          color: 'var(--color-good)',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--text-small)'
        }}>
          ✓ {successText || 'Done.'} {state?.data && state.data.already_done ? '(was already done)' : ''}
        </div>
      )}
      {failed && (
        <div style={{
          marginTop: 8,
          color: 'var(--color-bad)',
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--text-small)',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word'
        }}>
          ✗ {errorPrefix}: {state.error}
        </div>
      )}
    </div>
  );
}

// One step in the checklist on the left. Click toggles done state.
function StepRow({ index, title, done, current, onToggle, onJump }) {
  return (
    <li
      onClick={onJump}
      style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 10px',
        cursor: 'pointer',
        background: current ? 'var(--color-accent-soft)' : 'transparent',
        borderLeft: `3px solid ${current ? 'var(--color-accent)' : 'transparent'}`,
        fontSize: 'var(--text-body)'
      }}
    >
      <input
        type="checkbox"
        checked={done}
        onClick={(e) => e.stopPropagation()}
        onChange={onToggle}
      />
      <span style={{
        textDecoration: done ? 'line-through' : 'none',
        color: done ? 'var(--color-text-dim)' : 'var(--color-text)'
      }}>
        {index}. {title}
      </span>
    </li>
  );
}

const STEPS = [
  { key: 'client',     title: 'Add Client in xsiteio' },
  { key: 'numeric',    title: 'Capture Numeric ID' },
  { key: 'site',       title: 'Add Site fields' },
  { key: 'billing',    title: 'Configure Billing & Subscription' },
  { key: 'roles',      title: 'Set up Manager role (client login)' },
  { key: 'fuel',       title: 'Configure Fuel tab (client login)' },
  { key: 'inventory',  title: 'Enable Inventory Control (client login)' },
  { key: 'taxes',      title: 'Add Taxes: GST + PST (client login)' },
  { key: 'options',    title: 'Set Next PLU Number (client login)' },
  { key: 'userassign', title: 'Assign user to this site (client login)' },
];

// Provincial PST rates (excludes GST — those are federal 5%). Provinces
// with HST (ON, NB, NS, PE, NL) don't add a separate PST; the wizard
// notes that instead.
const PST_RATES = {
  BC: { rate: '0.07',    label: 'PST'      },
  SK: { rate: '0.06',    label: 'PST'      },
  MB: { rate: '0.07',    label: 'RST'      },
  QC: { rate: '0.09975', label: 'QST'      },
};
const HST_PROVINCES = new Set(['ON', 'NB', 'NS', 'PE', 'NL']);

function storageKey(siteId) {
  return `xsiteio_onboarding_${siteId}_v1`;
}

export default function XsiteioOnboardingDialog({ open, site, customer, subscription, onClose, onSaved }) {
  const [stepKey, setStepKey] = useState('client');
  const [doneMap, setDoneMap] = useState({});
  const [capturedSiteId, setCapturedSiteId] = useState('');
  const [savingId, setSavingId] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Restore checklist state from localStorage when the dialog opens.
  useEffect(() => {
    if (!open || !site) return;
    try {
      const raw = localStorage.getItem(storageKey(site.id));
      if (raw) {
        const parsed = JSON.parse(raw);
        setDoneMap(parsed.doneMap || {});
        setStepKey(parsed.stepKey || 'client');
      } else {
        setDoneMap({});
        setStepKey('client');
      }
      setCapturedSiteId(site.xsiteio_site_id ? String(site.xsiteio_site_id) : '');
      setSaveError(null);
    } catch (_) { /* localStorage unavailable — non-fatal */ }
  }, [open, site && site.id]);

  // Persist checklist state every time it changes.
  useEffect(() => {
    if (!open || !site) return;
    try {
      localStorage.setItem(storageKey(site.id), JSON.stringify({ doneMap, stepKey }));
    } catch (_) { /* non-fatal */ }
  }, [doneMap, stepKey, open, site && site.id]);

  // External ID generated from the site -- editable so Mary can adjust
  // if the pattern doesn't fit a specific customer.
  const [externalId, setExternalId] = useState('');
  useEffect(() => {
    if (open && site) setExternalId(generateExternalId(site));
  }, [open, site && site.id]);

  // Per-step API execution state. Keyed by step.key. Each entry:
  //   { running: bool, ok: bool|null, error: string|null, data: any }
  // Reset when the dialog re-opens on a different site so we don't
  // bleed stale results between customers.
  const [execState, setExecState] = useState({});
  useEffect(() => { if (open) setExecState({}); }, [open, site && site.id]);

  function setStepExec(key, patch) {
    setExecState((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
  }

  // POST to a per-site xsiteio onboarding endpoint and capture the
  // result + auto-tick the step's checkbox on success. Reused by all
  // three Execute buttons.
  async function runStep(stepKey, urlPath) {
    if (!site) return;
    setStepExec(stepKey, { running: true, ok: null, error: null });
    try {
      const r = await axios.post(`/api/sites/${site.id}/xsiteio/${urlPath}`);
      setStepExec(stepKey, { running: false, ok: true, error: null, data: r.data });
      setDoneMap((prev) => ({ ...prev, [stepKey]: true }));
      // The Capture Numeric ID step auto-completes when Add Client succeeds.
      if (stepKey === 'client') setDoneMap((prev) => ({ ...prev, numeric: true }));
      onSaved && onSaved();
    } catch (err) {
      const upstream = err.response?.data;
      const msg = upstream?.error || err.message || 'Request failed';
      const detail = upstream?.upstream_body
        ? (typeof upstream.upstream_body === 'string'
            ? upstream.upstream_body
            : JSON.stringify(upstream.upstream_body))
        : null;
      setStepExec(stepKey, {
        running: false, ok: false,
        error: detail ? `${msg} — ${detail}` : msg,
        data: null
      });
    }
  }

  const fullName = useMemo(() => {
    if (!site) return '';
    // "SK Centex Outlook cen00123" style site_name. xsiteio uses this
    // in its Name field on the Add Site form.
    const parts = [
      site.province_state ? String(site.province_state).toUpperCase() : null,
      site.site_name,
      site.site_number
    ].filter(Boolean);
    return parts.join(' ');
  }, [site]);

  if (!open || !site) return null;

  function toggleDone(key) {
    setDoneMap((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  async function saveCapturedSiteId() {
    const id = parseInt(capturedSiteId, 10);
    if (!Number.isInteger(id) || id <= 0) {
      setSaveError('Numeric ID must be a positive integer (the value xsiteio shows after Add Client).');
      return;
    }
    setSavingId(true);
    setSaveError(null);
    try {
      await axios.put(`/api/sites/${site.id}`, { xsiteio_site_id: id });
      // Mark the "capture" step done automatically once stored.
      setDoneMap((prev) => ({ ...prev, numeric: true }));
      onSaved && onSaved();
    } catch (err) {
      setSaveError(err.response?.data?.error || err.message);
    } finally {
      setSavingId(false);
    }
  }

  const step = STEPS.find((s) => s.key === stepKey) || STEPS[0];
  const stepIndex = STEPS.findIndex((s) => s.key === stepKey);

  // Right-pane content depends on which step is active.
  let body = null;
  if (stepKey === 'client') {
    const clientState = execState.client || {};
    const capturedClientId = clientState.data?.client_id || site.xsiteio_client_id;
    const capturedUserId   = clientState.data?.user_id   || site.xsiteio_admin_user_id;
    body = (
      <>
        <p style={{ marginTop: 0 }}>
          Click <strong>Execute</strong> to create the client in xsiteio via API.
          Or do it manually at <a href="https://xsiteio.com/dashboard/manage_clients" target="_blank" rel="noopener noreferrer">xsiteio.com/dashboard/manage_clients</a> with the External ID below.
        </p>
        <CopyRow label="External ID" value={externalId} />
        <ExecuteRow
          label="Add Client in xsiteio"
          onClick={() => runStep('client', 'create-client')}
          state={clientState}
          idDisplay={capturedClientId}
          successText={capturedClientId
            ? `client_id ${capturedClientId} · user_id ${capturedUserId || '—'}`
            : 'Client created in xsiteio.'}
          errorPrefix="Create client failed"
          site={site}
        />
      </>
    );
  } else if (stepKey === 'numeric') {
    body = (
      <>
        <p style={{ marginTop: 0 }}>
          After xsiteio confirms <em>"Client saved successfully"</em>, the new row appears at the top of the Clients list with a number in the first column. Type that number here so XSite Command can sync activity directly to this site.
        </p>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--space-2)' }}>
          <label style={{ flex: 1 }}>
            <div style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)', marginBottom: 4 }}>
              xsiteio Numeric ID
            </div>
            <input
              type="text"
              value={capturedSiteId}
              onChange={(e) => setCapturedSiteId(e.target.value.replace(/[^0-9]/g, ''))}
              className="cell-mono"
              placeholder="e.g. 68556698"
              style={{ width: '100%' }}
            />
          </label>
          <button
            type="button"
            className="btn-primary"
            onClick={saveCapturedSiteId}
            disabled={savingId || !capturedSiteId}
          >
            {savingId ? 'Saving…' : 'Save'}
          </button>
        </div>
        {saveError && (
          <p style={{ color: 'var(--color-bad)', fontSize: 'var(--text-small)', marginTop: 8 }}>
            {saveError}
          </p>
        )}
        {site.xsiteio_site_id && (
          <p style={{ color: 'var(--color-good)', fontSize: 'var(--text-small)', marginTop: 8 }}>
            ✓ Currently stored: {site.xsiteio_site_id}
          </p>
        )}
      </>
    );
  } else if (stepKey === 'site') {
    const siteState = execState.site || {};
    const capturedSiteId = siteState.data?.site_id || site.xsiteio_site_id;
    body = (
      <>
        <p style={{ marginTop: 0 }}>
          Click <strong>Execute</strong> to create the site and push every operational field below in one shot.
        </p>
        <ExecuteRow
          label="Create site + push settings"
          onClick={() => runStep('site', 'create-site')}
          state={siteState}
          idDisplay={capturedSiteId}
          successText={capturedSiteId
            ? `site_id ${capturedSiteId} — all fields pushed.`
            : 'Site created and all fields pushed.'}
          errorPrefix="Create site failed"
          site={site}
        />
        <p style={{ marginTop: 'var(--space-4)', fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
          For reference, here are the values being sent (matches what you'd type manually):
        </p>
        <CopyRow label="Numeric ID"             value={(site.site_number && parseInt((site.site_number.match(/\d+/) || ['0'])[0], 10)) || ''} hint="digits of site_number" />
        <CopyRow label="Name"                    value={fullName} />
        <CopyRow label="Address"                 value={site.address} />
        <CopyRow label="Phone Number"            value={site.site_phone} />
        <CopyRow label="Email Id"                value={customer && (customer.email1 || customer.email2 || '')} />
        <CopyRow label="City"                    value={site.city} />
        <CopyRow label="Country"                 value="Canada" />
        <CopyRow label="Province"                value={site.province_state} />
        <CopyRow label="Postal / ZIP"            value={site.postal_zip} />
        <CopyRow label="POS"                     value={posForXsiteio(site.pos_type)} />
        <CopyRow label="Inventory Control Method" value="Transaction" hint="default" />
        <CopyRow label="Sales Report Method"      value="Shift"       hint="default" />
        <CopyRow label="Reconciliation Fuel File Type"  value="FTP" hint="default" />
        <CopyRow label="Reconciliation Sales File Type" value="FTP" hint="default" />
        <CopyRow label="POS ID"                  value={site.site_number} hint="full site_number" />
      </>
    );
  } else if (stepKey === 'billing') {
    // Render the modules that XSite Command has annual_value > 0 for.
    // Mary toggles each one on in xsiteio's Billing & Subscription
    // dialog and types the yearly price + next charge date.
    const moduleRows = (() => {
      if (!subscription) return [];
      const m = (key, label, renewalKey) => {
        const annual = subscription[`${key}_annual`];
        if (!annual || Number(annual) <= 0) return null;
        return {
          label,
          annual: Number(annual).toFixed(2),
          renewal: subscription[renewalKey] || subscription.renewal_date || ''
        };
      };
      return [
        m('price_book',          'Price Book',           'price_book_renewal_date'),
        m('live_transactions',   'Real Time Inventory',  'live_transactions_renewal_date'),
        m('fuel_manager',        'Fuel Manager',         'fuel_manager_renewal_date'),
        m('charge',              'Charge Accounts',      'charge_renewal_date'),
        m('tx_exempt',           'Tax Exempt',           'tx_exempt_renewal_date'),
        m('car_wash',            'Car Wash',             'car_wash_renewal_date'),
        m('adv_report',          'Advanced Report',      'adv_report_renewal_date'),
        m('fuel_price',          'Fuel Price Changes',   'fuel_price_renewal_date'),
        m('case_break',          'Case Break',           'case_break_renewal_date'),
        m('dealer_comm',         'Dealer Commission',    'dealer_comm_renewal_date'),
        m('time_rest',           'Time Restrictions',    'time_rest_renewal_date'),
        m('sft_file',            'SFT File',             'sft_file_renewal_date'),
        m('private_label',       'Private Label Report', 'private_label_renewal_date'),
        m('imperial_import',     'Imperial Import',      'imperial_import_renewal_date'),
        m('fuel_grade_report',   'Fuel Grade Report',    'fuel_grade_report_renewal_date'),
        m('inventory_control',   'Inventory Control',    'inventory_control_renewal_date'),
      ].filter(Boolean);
    })();
    const billingState = execState.billing || {};
    const billingResult = billingState.data;
    body = (
      <>
        <p style={{ marginTop: 0 }}>
          Click <strong>Execute</strong> to enable every paid + free module on this site's subscription in xsiteio.
        </p>
        <ExecuteRow
          label="Enable subscription modules"
          onClick={() => runStep('billing', 'enable-billing')}
          state={billingState}
          successText={billingResult
            ? `${billingResult.enabled} enabled · ${billingResult.skipped} skipped (not subscribed) · ${billingResult.failed} failed`
            : 'All modules enabled.'}
          errorPrefix="Enable billing failed"
          site={site}
        />
        {billingResult && Array.isArray(billingResult.modules) && billingResult.modules.length > 0 && (
          <ul style={{ marginTop: 'var(--space-3)', listStyle: 'none', paddingLeft: 0 }}>
            {billingResult.modules.map((m, i) => (
              <li key={i} style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-small)', padding: '2px 0' }}>
                {m.status === 'enabled' ? '✓' : '✗'}{' '}
                <span style={{ display: 'inline-block', width: 170 }}>{m.module}</span>
                <span style={{ display: 'inline-block', width: 80, textAlign: 'right' }}>${Number(m.annual || 0).toFixed(2)}</span>
                <span style={{ color: 'var(--color-text-dim)', marginLeft: 12 }}>
                  {m.renewal ? new Date(m.renewal).toISOString().slice(0, 10) : '—'}
                </span>
                {m.error && <span style={{ color: 'var(--color-bad)', marginLeft: 12 }}>— {m.error}</span>}
              </li>
            ))}
          </ul>
        )}
        <p style={{ marginTop: 'var(--space-4)', fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
          For reference, here's what the local subscription currently has on file:
        </p>
        {moduleRows.length === 0 ? (
          <p className="cell-muted">
            No active modules on this site's subscription yet. Add the subscription in the Subscriptions tab first, then come back.
          </p>
        ) : (
          <div>
            {moduleRows.map((m) => (
              <div key={m.label} style={{
                display: 'grid',
                gridTemplateColumns: '1fr 100px 120px',
                gap: 'var(--space-3)',
                alignItems: 'center',
                padding: '6px 0',
                borderBottom: '1px solid var(--color-border)'
              }}>
                <div>{m.label}</div>
                <div className="cell-mono">${m.annual}</div>
                <div className="cell-mono">{m.renewal || '—'}</div>
              </div>
            ))}
          </div>
        )}
      </>
    );
  } else if (stepKey === 'roles') {
    const rolesState = execState.roles || {};
    const capturedRoleId = rolesState.data?.role_id || site.xsiteio_manager_role_id;
    body = (
      <>
        <p style={{ marginTop: 0 }}>
          Click <strong>Execute</strong> to create the Manager role, grant it ALL permission,
          and assign it to the auto-created client_admin user — all in one shot.
        </p>
        <ExecuteRow
          label="Set up Manager role"
          onClick={() => runStep('roles', 'manager-role')}
          state={rolesState}
          idDisplay={capturedRoleId}
          successText={capturedRoleId
            ? `role_id ${capturedRoleId} — permission granted, role assigned.`
            : 'Manager role created, permission granted, role assigned.'}
          errorPrefix="Manager role setup failed"
          site={site}
        />
        <p style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)', marginTop: 'var(--space-3)' }}>
          Role name: <code className="cell-mono">Manager</code> · Permission: <code className="cell-mono">ALL</code> · Object type: <code className="cell-mono">core.clients</code>
        </p>
      </>
    );
  } else if (stepKey === 'fuel') {
    const fuelState = execState.fuel || {};
    body = (
      <>
        <p style={{ marginTop: 0 }}>
          Click <strong>Execute</strong> to try pushing the Fuel-tab defaults to xsiteio
          via API. This is a <strong>speculative</strong> push -- xsiteio hasn't documented
          the Fuel-tab field names yet, so we send several likely variants
          (fuel_tax_rate / fuelTaxRate / tax_rate etc.) and see which one xsiteio honours.
          If the toast on xsiteio doesn't show the values after this, do it manually below.
        </p>
        <ExecuteRow
          label="Configure Fuel tab (speculative)"
          onClick={() => runStep('fuel', 'configure-fuel')}
          state={fuelState}
          successText={fuelState.data?.note || 'xsiteio accepted the payload.'}
          errorPrefix="Fuel tab push failed"
          site={site}
        />
        {fuelState.data?.attempted && (
          <details style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
            <summary style={{ cursor: 'pointer' }}>Show what we sent</summary>
            <pre style={{ background: 'var(--color-surface-sunken, #f8fafc)', padding: 8, borderRadius: 4, fontSize: 11, overflow: 'auto' }}>
              {JSON.stringify(fuelState.data.attempted, null, 2)}
            </pre>
          </details>
        )}
        <p style={{ marginTop: 'var(--space-4)', fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
          Manual fallback -- open xsiteio (still logged in as the client), navigate to the site's
          <strong> Fuel</strong> tab, fill these values, hit Save Fuel Settings:
        </p>
        <CopyRow label="Daily Recon Auto-Creation"  value="None"     hint="default" />
        <CopyRow label="Category Sales Retrieval"   value="Refund"   hint="default" />
        <CopyRow label="Fuel Tax Rate"              value="0.05"     hint="default 5%" />
        <CopyRow label="Auto Delivery"              value="Enabled"  hint="toggle on" />
      </>
    );
  } else if (stepKey === 'inventory') {
    body = (
      <>
        <p style={{ marginTop: 0 }}>
          Still logged in as the client. Find the <strong>Inventory Control</strong> section
          and flip the toggle to <strong>Enabled</strong>, then click <strong>Save Changes</strong>.
        </p>
        <CopyRow label="Inventory Control" value="Enabled" hint="toggle on" />
        <p style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)', marginTop: 'var(--space-3)' }}>
          Without this on, adjustments won't post for this client.
        </p>
      </>
    );
  } else if (stepKey === 'taxes') {
    const prov = String(site.province_state || '').toUpperCase().trim();
    const pst  = PST_RATES[prov];
    const isHst = HST_PROVINCES.has(prov);
    body = (
      <>
        <p style={{ marginTop: 0 }}>
          Still logged in as the client. Open the <strong>Taxes</strong> tab. Type each Name +
          Percentage below, then click <strong>+ Insert</strong> (or <strong>Save</strong> on
          existing rows). Leave <strong>Use in PO</strong> unchecked unless Dave says otherwise.
        </p>
        <CopyRow label="Tax Name #1"     value="GST"        hint="federal" />
        <CopyRow label="Percentage #1"   value="0.05"       hint="5% federal GST" />
        {pst && (
          <>
            <CopyRow label="Tax Name #2"   value={pst.label}                 hint={`provincial (${prov})`} />
            <CopyRow label="Percentage #2" value={pst.rate}                  hint={`${(Number(pst.rate) * 100).toFixed(pst.rate === '0.09975' ? 3 : 0)}% ${pst.label}`} />
          </>
        )}
        {isHst && (
          <p style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-small)', color: 'var(--color-warn)' }}>
            {prov} uses <strong>HST</strong> (combined federal + provincial) — no separate PST row needed.
            If the store still charges a split, confirm with Dave before proceeding.
          </p>
        )}
        {!pst && !isHst && (
          <p style={{ marginTop: 'var(--space-3)', fontSize: 'var(--text-small)', color: 'var(--color-text-dim)' }}>
            No provincial sales tax on file for <code>{prov || 'this province'}</code> — only GST needed.
            Confirm with Dave if unsure.
          </p>
        )}
      </>
    );
  } else if (stepKey === 'options') {
    body = (
      <>
        <p style={{ marginTop: 0 }}>
          Still logged in as the client. Open the <strong>Options</strong> tab. Find the
          <strong> Next PLU Number</strong> row, set the value below, and click <strong>Save</strong>.
        </p>
        <CopyRow label="Next PLU Number" value="9000000" hint="starting PLU" />
        <p style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)', marginTop: 'var(--space-3)' }}>
          9,000,000 keeps new PLUs well clear of anything the store scans off product barcodes.
        </p>
      </>
    );
  } else if (stepKey === 'userassign') {
    body = (
      <>
        <p style={{ marginTop: 0 }}>
          The client_admin user was auto-created when the client was added. Now go into
          <strong> User Settings</strong> for that user and lock them to this one site
          (so they only see their own data).
        </p>
        <CopyRow label="Email ID" value={externalId} />
        <CopyRow label="Name"     value={fullName} />
        <CopyRow label="Homepage" value="/dashboard" hint="leave blank if placeholder is fine" />
        <CopyRow label="Site"     value={fullName} hint="pick from dropdown" />
        <p style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)', marginTop: 'var(--space-3)' }}>
          The <strong>Site</strong> dropdown is what restricts them — leaving it blank means
          they can see every client's data. Save when the site is selected.
        </p>
      </>
    );
  }

  const allDone = STEPS.every((s) => doneMap[s.key]);

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(15,23,42,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-md)',
          width: 'min(1000px, 95vw)',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: 'var(--space-4) var(--space-5)',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <div>
            <h3 style={{ margin: 0 }}>xsiteio Onboarding</h3>
            <div style={{ color: 'var(--color-text-dim)', fontSize: 'var(--text-small)', marginTop: 4 }}>
              {fullName || `site #${site.id}`}
            </div>
          </div>
          <button type="button" className="btn-secondary btn-sm" onClick={onClose}>Close</button>
        </div>

        {/* Body — left = checklist, right = active step */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <ul style={{
            listStyle: 'none',
            margin: 0,
            padding: 'var(--space-3) 0',
            borderRight: '1px solid var(--color-border)',
            width: 280,
            overflowY: 'auto'
          }}>
            {STEPS.map((s, i) => (
              <StepRow
                key={s.key}
                index={i + 1}
                title={s.title}
                done={!!doneMap[s.key]}
                current={s.key === stepKey}
                onToggle={() => toggleDone(s.key)}
                onJump={() => setStepKey(s.key)}
              />
            ))}
            {allDone && (
              <li style={{ padding: 'var(--space-3)', color: 'var(--color-good)', fontSize: 'var(--text-small)' }}>
                ✓ All steps complete
              </li>
            )}
          </ul>
          <div style={{
            flex: 1, padding: 'var(--space-5)',
            overflowY: 'auto', minHeight: 0
          }}>
            <div style={{ fontSize: 'var(--text-small)', color: 'var(--color-text-dim)', marginBottom: 'var(--space-2)' }}>
              Step {stepIndex + 1} of {STEPS.length}
            </div>
            <h4 style={{ marginTop: 0 }}>{step.title}</h4>
            {body}
          </div>
        </div>

        {/* Footer — prev/next nav */}
        <div style={{
          padding: 'var(--space-3) var(--space-5)',
          borderTop: '1px solid var(--color-border)',
          display: 'flex', justifyContent: 'space-between'
        }}>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setStepKey(STEPS[Math.max(0, stepIndex - 1)].key)}
            disabled={stepIndex === 0}
          >
            ← Previous
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => setStepKey(STEPS[Math.min(STEPS.length - 1, stepIndex + 1)].key)}
            disabled={stepIndex === STEPS.length - 1}
          >
            Next →
          </button>
        </div>
      </div>
    </div>
  );
}
